<template>
  <div class="LayoutLogged" id="LayoutLogged">
    <baseLoading v-if="showMainLoading" :loading="showMainLoading" />
    <template v-else>
      <LeftBar
        id="left-side-bar"
        :class="{ active: showMenu }"
        @open="updateMenuStatus(true)"
        @close="updateMenuStatus(false)"
        :show="showMenu"
      />
      <div
        class="container-custom"
        :class="{
          'sm-hidden': showMenu,
          'sidebar-collapsed': !showMenu,
          'sidebar-expanded': showMenu,
          'assistant-docked': assistantDocked
        }"
      >
        <MaintenanceBanner />
        <Header />
        <main class="LayoutLogged__main">
          <div class="main__content">
            <transition name="fade" mode="out-in">
              <div
                v-if="
                  currentDepartment && currentDepartment.toLowerCase() !== 'all'
                "
              >
                <slot />
              </div>
              <div v-else class="flex justify-center items-center flex-col">
                <p class="mb-10">
                  No department selected, Please select a department
                </p>
                <BaseDepartmentSelector />
                <BaseStoreSelector class="mt-10" />
              </div>
            </transition>
          </div>
        </main>
        <!-- <BaseUserRequest /> -->
      </div>
      <AssistantBubble v-if="showAssistant" />
    </template>
  </div>
</template>
<script>
import Header from "@/components/layoutLogged/Header.vue";
import LeftBar from "@/components/layoutLogged/LeftBar.vue";
import MaintenanceBanner from "@/components/layoutLogged/MaintenanceBanner.vue";
import AssistantBubble from "@/modules/assistant/AssistantBubble.vue";
import { getSavedState, saveState } from "@/helpers/LocalStorage.js";
import { initChatSocket, disconnectChat } from "@/plugins/chat";
import { initPushListener } from "@/api/PushNotifications";

export default {
  name: "LayoutLogged",
  props: {
    name: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      storageChangeTimeout: null,
      isProcessingStorageChange: false,
      messagePingAudio: null
    };
  },
  mounted() {
    if (window.innerWidth < 640) {
      this.updateMenuStatus(false);
    }

    // Listen for localStorage changes from other tabs
    window.addEventListener("storage", this.handleStorageChange);

    // Initialize chat socket globally so new conversations are received on any page
    const token =
      this.$store.state.auth.currentUser &&
      this.$store.state.auth.currentUser.token;
    if (token) {
      initChatSocket(token);
    }

    // Listen for push notification messages from service worker
    initPushListener(() => {
      this.$router
        .push({
          name: "ChatPage",
          params: {
            storeCode: this.$route.params.storeCode,
            departmentCode: this.$route.params.departmentCode
          }
        })
        .catch(() => {});
    });
  },

  computed: {
    currentDepartment() {
      return this.$store.getters["general/getActiveDepartment"]?.code;
    },
    // En modo "todo el alto" el asistente es una columna más: la página se
    // corre a la izquierda para dejarle sitio
    assistantDocked() {
      return this.$store.getters["general/getAssistantDocked"];
    },
    // Busca y cotiza en una tienda: sin departamento no hay catálogo
    showAssistant() {
      return (
        this.currentDepartment &&
        this.currentDepartment.toLowerCase() !== "all" &&
        this.$store.getters["auth/userHasPermission"]("can_use_ia_features")
      );
    },
    // Tracks total message count across all conversations to detect new messages
    totalMessageCount() {
      const messages = this.$store.state.chat.messages;
      return Object.values(messages).reduce((sum, arr) => sum + arr.length, 0);
    },
    lastTransfer() {
      return this.$store.state.chat.lastTransfer;
    },
    showMenu() {
      return this.$store.getters["general/getStatusMenu"];
    },
    showMainLoading() {
      return this.$store.getters["general/getMainLoading"];
    }
  },
  watch: {
    lastTransfer(data) {
      if (!data) return;
      const token =
        this.$store.state.auth.currentUser &&
        this.$store.state.auth.currentUser.token;
      if (!token) return;
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload.id === data.newAgentId) {
          this.$toast.info("A chat has been assigned to you", {
            position: "top-right",
            duration: 5000
          });
          try {
            const audio = new Audio(
              require("@/assets/music/freesound_community-vibrating-message-37619.mp3")
            );
            audio.play();
          } catch (e) {
            // ignore
          }
        }
      } catch (e) {
        // ignore
      }
    },
    totalMessageCount(newVal, oldVal) {
      if (newVal <= oldVal) return;

      // Find the conversation that has the new message (last added)
      const messages = this.$store.state.chat.messages;
      let newMsgConvId = null;
      for (const [convId, msgs] of Object.entries(messages)) {
        if (msgs.length > 0) {
          const last = msgs[msgs.length - 1];
          // Only react to client messages (not our own sent messages)
          if (last.senderType === "client" || last.sender_type === "client") {
            newMsgConvId = Number(convId);
            break;
          }
        }
      }

      if (!newMsgConvId) return;

      // Determine if user is focused on that specific conversation
      const isOnChatPage = this.$route.name === "ChatPage";
      const selectedId = this.$store.state.chat.selectedConversationId;
      const isFocusedOnConv =
        isOnChatPage && selectedId === newMsgConvId && !document.hidden;

      if (!isFocusedOnConv) {
        try {
          if (!this.messagePingAudio) {
            this.messagePingAudio = new Audio(
              require("@/assets/music/universfield-happy-message-ping-351298.mp3")
            );
          }
          this.messagePingAudio.currentTime = 0;
          this.messagePingAudio.play();
        } catch (e) {
          // ignore
        }
      }
    }
  },
  async beforeMount() {
    const storeParam = this.$route.params.storeCode;
    const deptParam = this.$route.params.departmentCode;

    if (deptParam !== "ALL") {
      if (storeParam) {
        saveState("store.selectedStore", storeParam);
      }
      if (deptParam) {
        saveState("store.selectedDepartment", deptParam);
      }
      await this.$store.dispatch("general/init", {
        store: storeParam,
        departmentCode: deptParam
      });
    } else {
      const savedDepartment = getSavedState("store.selectedDepartment");
      if (savedDepartment && savedDepartment.toLowerCase() !== "all") {
        await this.$store.dispatch("general/init", {
          store: storeParam,
          departmentCode: savedDepartment
        });
      } else {
        await this.$store.dispatch("general/init", {
          store: storeParam,
          departmentCode: "ALL"
        });
      }
    }
  },
  components: {
    Header,
    LeftBar,
    MaintenanceBanner,
    AssistantBubble
  },
  methods: {
    toggleMenu(status) {
      this.showMenu = status;
    },
    updateMenuStatus(newStatus) {
      this.$store.dispatch("general/updateShowMenu", newStatus);
    },
    async goToForbiden() {
      await this.$store.dispatch("auth/logOut");
      this.$router.push({ name: "loginPage" });
    },
    handleStorageChange(event) {
      // Only respond to changes in selectedDepartment or selectedStore
      if (
        event.key === "store.selectedDepartment" ||
        event.key === "store.selectedStore"
      ) {
        // If already processing a storage change, ignore this one
        if (this.isProcessingStorageChange) {
          console.log("Storage change already in progress, ignoring...");
          return;
        }

        console.log(
          "Storage changed in another tab:",
          event.key,
          event.newValue
        );

        // Set flag to prevent multiple simultaneous processing
        this.isProcessingStorageChange = true;

        // Clear any existing timeout to debounce rapid changes
        if (this.storageChangeTimeout) {
          clearTimeout(this.storageChangeTimeout);
        }

        // Add a delay to prevent rapid-fire navigation between tabs
        this.storageChangeTimeout = setTimeout(() => {
          // Get current values from localStorage
          const savedDepartment =
            getSavedState("store.selectedDepartment") || "ALL";
          const savedStore = getSavedState("store.selectedStore");

          // Check if the current URL matches the localStorage values
          const currentStore = this.$route.params.storeCode;
          const currentDepartment = this.$route.params.departmentCode;

          // If localStorage values are different from current URL, navigate to the new URL
          if (
            savedStore &&
            (savedStore !== currentStore ||
              savedDepartment !== currentDepartment)
          ) {
            console.log("Syncing URL with localStorage values:", {
              savedStore,
              savedDepartment
            });

            // Navigate to the new URL with updated values
            this.$router
              .push({
                name: this.$route.name,
                params: {
                  ...this.$route.params,
                  storeCode: savedStore,
                  departmentCode: savedDepartment
                }
              })
              .then(() => {
                // Reset flag after successful navigation
                this.isProcessingStorageChange = false;
              })
              .catch(() => {
                // Reset flag even if navigation fails
                this.isProcessingStorageChange = false;
              });
          } else {
            // Reset flag if no navigation needed
            this.isProcessingStorageChange = false;
          }
        }, 1500); // 500ms delay to prevent tab fighting
      }
    }
  },
  beforeDestroy() {
    // Clean up the storage event listener
    window.removeEventListener("storage", this.handleStorageChange);
    disconnectChat();

    // Clear any pending timeout
    if (this.storageChangeTimeout) {
      clearTimeout(this.storageChangeTimeout);
    }
  }
};
</script>
<style lang="scss">
@import "@/assets/style/css/generic-variables.scss";

.LayoutLogged {
  display: flex;
  flex-direction: row;
  min-height: 100vh;

  .container-custom {
    flex: 1;
    transition: all 0.3s ease-in-out;

    // Default: sidebar collapsed (72px width)
    &.sidebar-collapsed {
      margin-left: 72px;
    }

    // Sidebar expanded (280px width)
    &.sidebar-expanded {
      margin-left: 280px;
    }

    // El ancho del panel del asistente acoplado (AssistantBubble.vue)
    &.assistant-docked {
      margin-right: 420px;
    }

    // Mobile responsive
    @media (max-width: 639px) {
      &.sidebar-collapsed,
      &.sidebar-expanded {
        margin-left: 0;
      }

      &.sm-hidden {
        display: none;
      }
    }
  }

  .LayoutLogged__main {
    .main__content {
      width: 100%;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  transition: opacity 0s;
  opacity: 0;
}

// Legacy mobile styles (keep for compatibility)
@media (max-width: 700px) {
  .sm-hidden {
    display: none;
  }
  #left-side-bar.active {
    width: 100%;
  }
}
</style>
